import { useEffect, useState } from 'react';
import type { Bridge } from '../../contracts/bridge';
import type { MitigationPlan } from '../../contracts/mitigation';
import { TREATMENT_MATERIAL_LABELS, TreatmentMaterial } from '../../contracts/mitigation';
import { apiClient } from '../../core/apiClient';

interface MitigationPanelProps {
  bridge: Bridge;
  mitigation: MitigationPlan | null;
}

interface MitigationResponse {
  recommendation: {
    material: string;
    apply_by: string;
    quantity: number;
    units: string;
    expected_effect: string;
    citations: Array<{ source: string; reference: string }>;
  } | null;
  reason?: string;
}

export function MitigationPanel({ bridge }: MitigationPanelProps) {
  const [response, setResponse] = useState<MitigationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadMitigation();
  }, [bridge.bridge_id]);

  const loadMitigation = async () => {
    setIsLoading(true);
    try {
      const data = await apiClient.get<MitigationResponse>(
        `/engine/mitigation/${bridge.bridge_id}`
      );
      setResponse(data);
    } catch (err) {
      console.error('Failed to load mitigation:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const rec = response?.recommendation;

  return (
    <div>
      <div className="panel-header">
        <h3 className="panel-title">Mitigation</h3>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <div className="loading-spinner" />
        </div>
      )}

      {!isLoading && !rec && (
        <div className="text-sm text-secondary">
          {response?.reason || 'No treatment recommended for current conditions.'}
        </div>
      )}

      {!isLoading && rec && (
        <div className="flex flex-col gap-3">
          <div className="card">
            <div className="text-sm text-secondary mb-1">Recommended Treatment</div>
            <div className="font-semibold">
              {TREATMENT_MATERIAL_LABELS[rec.material as TreatmentMaterial] ||
                rec.material}
            </div>
          </div>

          <div className="text-sm">
            <strong>Apply by:</strong>{' '}
            {new Date(rec.apply_by).toLocaleString()}
          </div>

          <div className="text-sm">
            <strong>Quantity:</strong> {rec.quantity} {rec.units}
          </div>

          <div className="text-sm text-secondary">{rec.expected_effect}</div>

          {rec.citations.length > 0 && (
            <div className="text-xs text-secondary mt-2">
              Sources: {rec.citations.map((c) => c.source).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
